import { FC, useState } from 'react';
import { Box, Chip, CircularProgress, Collapse, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useContentDirection } from '@hooks';
import { Trans, useTrans } from '@libs/i18n';
import {
  Assessment,
  DomainComparisonEntry,
  DomainDocumentComparisonChangeType,
} from '@libs/types';
import {
  CaretRightIcon,
  CheckCircleIcon,
  DownloadSimpleIcon,
  FileTextIcon,
  InfoIcon,
  PencilSimpleIcon,
  ProhibitIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { NoFlipBox } from '@utils';

import { useGetDocumentDownloadUrl } from '../../api/documents';
import { DomainResultViewModel } from '../../types';

import { DeltaTag } from './DeltaTag';

interface DomainResultItemProps {
  domain: DomainResultViewModel;
  /** Comparison entry for this domain when a baseline is selected. */
  compareEntry?: DomainComparisonEntry;
  /** Active version metadata — for badge labels. */
  activeVersion?: Assessment;
  /** Baseline version metadata — for badge labels. */
  baselineVersion?: Assessment;
  /**
   * In-progress draft sibling. When present, the per-domain "Edit" button jumps
   * into the draft's workflow for this domain so the user can re-upload
   * documents. When absent, the button is still shown but, on click, prompts the
   * user to start a new version (via {@link onRequestNewVersion}).
   */
  draftVersion?: Assessment;
  /** Called when a user clicks "Edit" but no draft exists yet. */
  onRequestNewVersion?: () => void;
}

const hasExpandableContent = (domain: DomainResultViewModel): boolean =>
  domain.identifiedGaps.length > 0 ||
  !!domain.theRisk?.trim() ||
  domain.whatIsNeeded.length > 0 ||
  domain.improvementPriorities.length > 0 ||
  domain.documents.length > 0 ||
  domain.missingDocumentsList.length > 0;

const isSkippedNoDocs = (domain: DomainResultViewModel): boolean =>
  domain.isSkipped && domain.documents.length === 0;

export const DomainResultItem: FC<DomainResultItemProps> = observer(({
  domain,
  compareEntry,
  activeVersion,
  baselineVersion,
}) => {
  const theme = useTheme();
  // The findings below are AI prose frozen in the assessment's language; the
  // card around them stays in the UI's direction.
  const { dir: contentDir, lang: contentLang } = useContentDirection();
  const { t } = useTrans();
  const [open, setOpen] = useState(false);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);
  const downloadMutation = useGetDocumentDownloadUrl();

  const scoreColor = domain.maturityConfig.color.hex;
  const isNotApplicable = !domain.isApplicable;
  // Not-applicable domains never expand — backend strips analysis/docs/missing
  // so there is nothing to reveal even if hasExpandableContent returned true.
  const canExpand = !isNotApplicable && hasExpandableContent(domain);
  const skippedNoDocs = !isNotApplicable && isSkippedNoDocs(domain);

  const totalDocCount = domain.documents.length + domain.missingDocumentsList.length;

  // ============================================================
  // Comparison-derived values (only set when baseline is picked)
  // ============================================================
  const isComparing = !!compareEntry && !!baselineVersion;
  const baselineLabel = baselineVersion ? `V${baselineVersion.version}` : '';
  const activeLabel = activeVersion ? `V${activeVersion.version}` : '';
  const baselineScore = isComparing
    ? Math.round(compareEntry!.baselineResults?.maturity.percentage ?? 0)
    : null;
  const wasEdited = isComparing ? compareEntry!.changedInComparison : false;

  // Build a lookup of active-version document changeType, keyed by file name
  // (document id is the most reliable, fall back to fileName).
  const docChangeByDocId = new Map<string, DomainDocumentComparisonChangeType>();
  if (isComparing) {
    compareEntry!.comparisonDocuments.forEach((entry) => {
      docChangeByDocId.set(entry.documentResult.document.id, entry.changeType);
    });
  }
  const removedDocs = isComparing
    ? compareEntry!.baselineDocuments.filter(
      (e) => e.changeType === DomainDocumentComparisonChangeType.Removed,
    )
    : [];

  return (
    <Box>
      {/* Clickable header row */}
      <Box
        onClick={canExpand ? () => setOpen((v) => !v) : undefined}
        sx={{
          px: 0.75,
          py: 0.75,
          mx: -0.75,
          borderRadius: '8px',
          cursor: canExpand ? 'pointer' : 'default',
          '&:hover': canExpand ? { backgroundColor: '#FAFAFA' } : undefined,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* Top row: chevron + name + badges + score */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '24px 1fr auto', alignItems: 'center', gap: 1.5 }}>
          {/* Chevron */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.tertiary',
              transition: 'transform 0.2s ease',
              transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
              visibility: canExpand ? 'visible' : 'hidden',
            }}
          >
            <CaretRightIcon size={16} />
          </Box>

          {/* Name + badges */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="textMD_semiBold" sx={{ color: 'text.primary' }}>
              {domain.domainName}
            </Typography>

            {/* Weight badge — always shown */}
            <Chip
              size="small"
              label={t('assessments.overview.weightBadge', { weight: (domain.weight * 100).toFixed(1) })}
              sx={{
                height: 22,
                fontSize: 11.5,
                fontWeight: 500,
                backgroundColor: '#FAF5FD',
                color: theme.palette.violet[700],
                border: `1px solid #E8D4F8`,
                borderRadius: 999,
                '& .MuiChip-label': { px: 1 },
              }}
            />

            {/* Not Applicable badge — takes precedence over Skipped */}
            {isNotApplicable && (
              <Chip
                size="small"
                icon={<ProhibitIcon size={12} />}
                label={t('assessments.overview.domainNotApplicableBadge')}
                sx={{
                  height: 22,
                  fontSize: 11.5,
                  fontWeight: 500,
                  backgroundColor: '#F5F5F5',
                  color: 'text.secondary',
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 999,
                  '& .MuiChip-label': { px: 1 },
                  '& .MuiChip-icon': { ml: 0.625, color: 'text.secondary' },
                }}
              />
            )}

            {/* Skipped badge */}
            {!isNotApplicable && domain.isSkipped && (
              <Chip
                size="small"
                label={t('assessments.summary.skipped')}
                sx={{
                  height: 22,
                  fontSize: 11.5,
                  fontWeight: 500,
                  backgroundColor: '#FFFBE6',
                  color: theme.palette.warning.main,
                  border: `1px solid #FFE58F`,
                  borderRadius: 999,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            )}

            {/* Missing documents badge */}
            {domain.missingDocumentsList.length > 0 && (
              <Chip
                size="small"
                label={t('assessments.overview.missingDocumentsBadge', { count: domain.missingDocumentsList.length })}
                sx={{
                  height: 22,
                  fontSize: 11.5,
                  fontWeight: 500,
                  backgroundColor: '#FFE9E7',
                  color: theme.palette.error.main,
                  border: `1px solid #FAC8C3`,
                  borderRadius: 999,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            )}

            {/* Uploaded documents badge — "X / Y Uploaded documents" so users
                know this is uploaded coverage, not a total file count. */}
            {domain.documents.length > 0 && (
              <Chip
                size="small"
                label={t('assessments.overview.documentsBadge', {
                  count: domain.documents.length,
                  total: totalDocCount,
                })}
                sx={{
                  height: 22,
                  fontSize: 11.5,
                  fontWeight: 500,
                  backgroundColor: '#EFF8FF',
                  color: theme.palette.blue[700],
                  border: `1px solid #DAEEFF`,
                  borderRadius: 999,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            )}

            {/* Comparison badge — Edited in V<active> or Same as V<baseline> */}
            {isComparing && wasEdited && (
              <Chip
                size="small"
                icon={<PencilSimpleIcon size={12} />}
                label={t('assessments.overview.versioning.domainBreakdown.editedIn', { version: activeLabel })}
                sx={{
                  height: 22,
                  fontSize: 11.5,
                  fontWeight: 600,
                  backgroundColor: '#FAF5FD',
                  color: theme.palette.violet[700],
                  border: '1px solid #E8D4F8',
                  borderRadius: 999,
                  '& .MuiChip-label': { px: 1 },
                  '& .MuiChip-icon': { ml: 0.625, color: theme.palette.violet[700] },
                }}
              />
            )}
            {isComparing && !wasEdited && (
              <Chip
                size="small"
                label={t('assessments.overview.versioning.domainBreakdown.sameAs', { version: baselineLabel })}
                sx={{
                  height: 22,
                  fontSize: 11.5,
                  fontWeight: 500,
                  backgroundColor: '#F5F5F5',
                  color: 'text.secondary',
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 999,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            )}
          </Box>

          {/* Score (+ optional delta tag). Hidden for not-applicable domains —
              there is no meaningful score to render. */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            {!isNotApplicable && (
              <>
                <Typography
                  variant="textMD_semiBold"
                  sx={{ color: scoreColor, alignSelf: 'flex-end' }}
                >
                  {domain.maturityScore.toFixed(0)}%
                </Typography>
                {isComparing && baselineScore != null && (
                  <DeltaTag
                    now={Math.round(domain.maturityScore)}
                    prev={baselineScore}
                    baselineLabel={baselineLabel}
                  />
                )}
              </>
            )}
            {isNotApplicable && (
              <Typography
                variant="textMD_semiBold"
                sx={{ color: 'text.tertiary', alignSelf: 'flex-end' }}
              >
                —
              </Typography>
            )}
          </Box>
        </Box>

        {/* Progress bar (with optional ghost overlay showing baseline score) */}
        {!isNotApplicable && (
        <Box sx={{ pl: '36px' }}>
          <Box
            sx={{
              position: 'relative',
              height: 6,
              borderRadius: 999,
              backgroundColor: '#F0F0F2',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                width: `${Math.min(100, Math.max(0, domain.maturityScore))}%`,
                backgroundColor: scoreColor,
                borderRadius: 999,
                transition: 'width 0.3s ease',
              }}
            />
            {isComparing && baselineScore != null && (
              <>
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: `${Math.min(100, Math.max(0, baselineScore))}%`,
                    backgroundColor: scoreColor,
                    opacity: 0.8,
                    backgroundImage:
                      'repeating-linear-gradient(45deg, transparent 0 3px, rgba(255,255,255,0.55) 3px 6px)',
                    borderRadius: 999,
                  }}
                />
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    top: -1,
                    bottom: -1,
                    left: `${Math.min(100, Math.max(0, baselineScore))}%`,
                    width: 3,
                    backgroundColor: scoreColor,
                    opacity: 1,
                    transform: 'translateX(-1.5px)',
                    boxShadow: '0 0 4px 1px rgba(0,0,0,0.18)',
                  }}
                />
              </>
            )}
          </Box>
        </Box>
        )}

        {/* Gaps / skipped meta row (+ optional baseline score legend). For
            not-applicable domains we show a single "not applicable" caption
            instead of a gaps count or the score legend. */}
        <Box sx={{ pl: '36px', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {isNotApplicable ? (
            <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
              <Trans i18nKey="assessments.overview.domainNotApplicableCaption" />
            </Typography>
          ) : skippedNoDocs ? (
            <Typography variant="textXS_regular" sx={{ color: theme.palette.warning.main }}>
              <Trans i18nKey="assessments.overview.domainNotAssessed" />
            </Typography>
          ) : (
            <>
              <Box
                sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: scoreColor, flexShrink: 0 }}
              />
              <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
                {t('assessments.overview.gapsCount', { count: domain.gaps })}
              </Typography>
            </>
          )}
          {!isNotApplicable && isComparing && baselineScore != null && (
            <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
              · {baselineLabel}: {baselineScore}%
            </Typography>
          )}

        </Box>
      </Box>

      {/* Not-applicable empty state — always visible (no expand needed). */}
      {isNotApplicable && (
        <Box
          sx={{
            ml: '36px',
            mt: 1.5,
            mb: 0.75,
            display: 'flex',
            gap: 1.75,
            alignItems: 'flex-start',
            p: 2,
            border: `1px dashed ${theme.palette.grey[300]}`,
            backgroundColor: '#FAFAFA',
            borderRadius: '12px',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: '#F5F5F5',
              color: 'text.secondary',
              border: `1px solid ${theme.palette.grey[300]}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ProhibitIcon size={18} />
          </Box>
          <Box>
            <Typography variant="textSM_semiBold" sx={{ color: 'text.primary', display: 'block', mb: 0.5 }}>
              <Trans i18nKey="assessments.overview.domainNotApplicableTitle" />
            </Typography>
            <Typography variant="textSM_regular" sx={{ color: 'text.secondary' }}>
              <Trans i18nKey="assessments.overview.domainNotApplicableDescription" />
            </Typography>
          </Box>
        </Box>
      )}

      {/* Expandable detail panel */}
      <Collapse in={open}>
        <Box
          sx={{
            ml: '36px',
            mt: 1.75,
            mb: 0.75,
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: '12px',
            backgroundColor: '#FAFAFA',
            overflow: 'hidden',
          }}
        >
          {/* Skipped empty state */}
          {skippedNoDocs && (
            <Box
              sx={{
                display: 'flex',
                gap: 1.75,
                alignItems: 'flex-start',
                p: 2.5,
                border: '1px dashed #FFE58F',
                backgroundColor: '#FFFDF5',
                borderRadius: '12px',
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  backgroundColor: '#FFFBE6',
                  color: theme.palette.warning.main,
                  border: '1px solid #FFE58F',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <InfoIcon size={18} />
              </Box>
              <Box>
                <Typography variant="textSM_semiBold" sx={{ color: 'text.primary', display: 'block', mb: 0.5 }}>
                  <Trans i18nKey="assessments.overview.domainSkippedTitle" />
                </Typography>
                <Typography variant="textSM_regular" sx={{ color: 'text.secondary' }}>
                  <Trans i18nKey="assessments.overview.domainSkippedDescription" />
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
                  {domain.missingDocumentsList.length > 0 && (
                    <Chip
                      size="small"
                      label={t('assessments.overview.standardsAwaitingUpload', { count: domain.missingDocumentsList.length })}
                      sx={{
                        height: 22, fontSize: 11.5, fontWeight: 500,
                        backgroundColor: '#FFE9E7', color: theme.palette.error.main,
                        border: '1px solid #FAC8C3', borderRadius: 999,
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  )}
                  <Chip
                    size="small"
                    label={t('assessments.overview.domainWeight', { weight: (domain.weight * 100).toFixed(1) })}
                    sx={{
                      height: 22, fontSize: 11.5, fontWeight: 500,
                      backgroundColor: '#FAF5FD', color: theme.palette.violet[700],
                      border: '1px solid #E8D4F8', borderRadius: 999,
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* Two-column detail grid — non-skipped domains only */}
          {!skippedNoDocs && (
            <>
              {/* Two-column detail grid of AI-generated findings. Pinned to the
                  language they were written in — including these columns' own
                  headings, so the block reads as one piece instead of mixing
                  directions mid-card. The card around it stays in the UI's
                  direction. */}
              {(domain.identifiedGaps.length > 0 || domain.theRisk || domain.whatIsNeeded.length > 0 || domain.improvementPriorities.length > 0) && (
                <NoFlipBox
                  mode={contentDir}
                  lang={contentLang ?? undefined}
                  sx={{ display: 'flex', gap: 0 }}
                >
                  {/* Left column: Identified Gaps + The Risk */}
                  {(domain.identifiedGaps.length > 0 || domain.theRisk) && (
                    <Box
                      sx={{
                        flex: 1.15,
                        p: 2,
                        borderRight: `1px dashed ${theme.palette.grey[300]}`,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      {/* Identified Gaps */}
                      {domain.identifiedGaps.length > 0 && (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                            <WarningIcon size={14} color={theme.palette.text.secondary} />
                            <Typography
                              variant="textXS_semiBold"
                              sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}
                            >
                              <Trans i18nKey="assessments.overview.gapsSummary" />
                            </Typography>
                            <Chip
                              size="small"
                              label={domain.identifiedGaps.length}
                              sx={{
                                height: 18, fontSize: 11, fontWeight: 600,
                                backgroundColor: '#fff', color: 'text.secondary',
                                border: `1px solid ${theme.palette.grey[300]}`,
                                borderRadius: 999, '& .MuiChip-label': { px: 0.875 },
                              }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                            {domain.identifiedGaps.slice(0, 5).map((gap, i) => (
                              <Box
                                key={i}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: '22px 1fr',
                                  gap: 1.25,
                                  pt: i === 0 ? 0.5 : 1.25,
                                  pb: 1.25,
                                  borderTop: i === 0 ? 'none' : `1px dashed #E5E5E8`,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 22, height: 22, borderRadius: '6px',
                                    backgroundColor: '#fff', border: `1px solid ${theme.palette.grey[300]}`,
                                    color: 'text.secondary',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 11, fontWeight: 600, flexShrink: 0,
                                  }}
                                >
                                  {i + 1}
                                </Box>
                                <Box>
                                  <Typography variant="textSM_semiBold" sx={{ color: 'text.primary', display: 'block', mb: 0.25 }}>
                                    {gap.title}
                                  </Typography>
                                  <Typography variant="textSM_regular" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                                    {gap.description}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* The Risk */}
                      {domain.theRisk && (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                            <InfoIcon size={14} color={theme.palette.text.secondary} />
                            <Typography
                              variant="textXS_semiBold"
                              sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}
                            >
                              <Trans i18nKey="assessments.overview.theRisk" />
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '18px 1fr',
                              gap: 1.25,
                              backgroundColor: '#FFF6F1',
                              border: '1px solid #FAD4BC',
                              borderRadius: '10px',
                              p: 1.75,
                            }}
                          >
                            <Box sx={{ color: '#E87040', mt: 0.25 }}>
                              <WarningIcon size={16} />
                            </Box>
                            <Box>
                              <Typography
                                variant="textXS_semiBold"
                                sx={{
                                  color: '#E87040',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  display: 'block',
                                  mb: 0.5,
                                }}
                              >
                                <Trans i18nKey="assessments.overview.ifLeftUnaddressed" />
                              </Typography>
                              <Typography variant="textSM_regular" sx={{ color: 'text.primary', lineHeight: 1.55 }}>
                                {domain.theRisk}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Right column: What is Needed + Improvement Priorities */}
                  {(domain.whatIsNeeded.length > 0 || domain.improvementPriorities.length > 0) && (
                    <Box
                      sx={{
                        flex: 1,
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      {/* What is Needed */}
                      {domain.whatIsNeeded.length > 0 && (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                            <CheckCircleIcon size={14} color={theme.palette.text.secondary} />
                            <Typography
                              variant="textXS_semiBold"
                              sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}
                            >
                              <Trans i18nKey="assessments.overview.whatIsNeeded" />
                            </Typography>
                            <Chip
                              size="small"
                              label={domain.whatIsNeeded.length}
                              sx={{
                                height: 18, fontSize: 11, fontWeight: 600,
                                backgroundColor: '#fff', color: 'text.secondary',
                                border: `1px solid ${theme.palette.grey[300]}`,
                                borderRadius: 999, '& .MuiChip-label': { px: 0.875 },
                              }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {[...domain.whatIsNeeded]
                              .sort((a, b) => a.orderIndex - b.orderIndex)
                              .map((item, i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '24px 1fr',
                                    gap: 1.25,
                                    p: 1.25,
                                    backgroundColor: '#fff',
                                    border: `1px solid ${theme.palette.grey[200]}`,
                                    borderRadius: '8px',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 24, height: 24, borderRadius: '50%',
                                      backgroundColor: '#EFF8FF',
                                      color: theme.palette.blue[700],
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      fontSize: 11, fontWeight: 600, flexShrink: 0,
                                    }}
                                  >
                                    {item.orderIndex}
                                  </Box>
                                  <Box>
                                    <Typography variant="textSM_semiBold" sx={{ color: 'text.primary', display: 'block', mb: 0.25 }}>
                                      {item.title}
                                    </Typography>
                                    <Typography variant="textSM_regular" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                                      {item.description}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                          </Box>
                        </Box>
                      )}

                      {/* Improvement Priorities */}
                      {domain.improvementPriorities.length > 0 && (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                            <FileTextIcon size={14} color={theme.palette.text.secondary} />
                            <Typography
                              variant="textXS_semiBold"
                              sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}
                            >
                              <Trans i18nKey="assessments.overview.improvementPriorities" />
                            </Typography>
                            <Chip
                              size="small"
                              label={domain.improvementPriorities.length}
                              sx={{
                                height: 18, fontSize: 11, fontWeight: 600,
                                backgroundColor: '#fff', color: 'text.secondary',
                                border: `1px solid ${theme.palette.grey[300]}`,
                                borderRadius: 999, '& .MuiChip-label': { px: 0.875 },
                              }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {[...domain.improvementPriorities]
                              .sort((a, b) => a.orderIndex - b.orderIndex)
                              .map((item, i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '18px 1fr',
                                    gap: 1.25,
                                    alignItems: 'center',
                                    p: 1,
                                    pl: 1.25,
                                    backgroundColor: '#fff',
                                    border: `1px solid ${theme.palette.grey[200]}`,
                                    borderRadius: '6px',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 18, height: 18, borderRadius: '50%',
                                      backgroundColor: '#0A0A0A', color: '#fff',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      fontSize: 10, fontWeight: 600, flexShrink: 0,
                                    }}
                                  >
                                    {item.orderIndex}
                                  </Box>
                                  <Typography variant="textSM_regular" sx={{ color: 'text.primary' }}>
                                    {item.title}
                                  </Typography>
                                </Box>
                              ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </NoFlipBox>
              )}

            </>
          )}

          {/* Documents section — always shown when there's content, including skipped domains */}
          {(domain.documents.length > 0 || domain.missingDocumentsList.length > 0 || removedDocs.length > 0) && (
            <Box
              sx={{
                p: 2,
                borderTop: `1px dashed ${theme.palette.grey[300]}`,
              }}
            >
              {/* Section header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <FileTextIcon size={14} color={theme.palette.text.secondary} />
                <Typography
                  variant="textXS_semiBold"
                  sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}
                >
                  <Trans i18nKey="assessments.overview.assessedDocuments" />
                </Typography>
                {/* Show uploaded / total so users see assessed coverage at
                    a glance instead of just the standards count. */}
                <Chip
                  size="small"
                  label={`${domain.documents.length} / ${totalDocCount}`}
                  sx={{
                    height: 18, fontSize: 11, fontWeight: 600,
                    backgroundColor: '#fff', color: 'text.secondary',
                    border: `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: 999, '& .MuiChip-label': { px: 0.875 },
                  }}
                />
              </Box>

              {/* Uploaded documents */}
              {domain.documents.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {domain.documents.map((doc, i) => {
                    const aDoc = doc.document;
                    const stdDoc = aDoc.standardDocument;
                    const fileSizeKb = (aDoc.fileSize / 1024).toFixed(1);
                    const uploadedDate = new Intl.DateTimeFormat('en-GB', {
                      month: 'short', day: 'numeric', year: 'numeric',
                    }).format(new Date(aDoc.uploadedAt));

                    const docChange = isComparing ? docChangeByDocId.get(aDoc.id) : undefined;
                    const isNew = docChange === DomainDocumentComparisonChangeType.Added;

                    return (
                      <Box
                        key={i}
                        sx={{
                          border: `1px solid ${theme.palette.grey[200]}`,
                          backgroundColor: '#fff',
                          borderRadius: '12px',
                          overflow: 'hidden',
                        }}
                      >
                        {/* === Uploaded file section ===
                            Top half describes the file the user actually
                            uploaded: name, version-diff tag (when comparing),
                            size/date, and download. Standard-document chips
                            were moved out — they describe the classification,
                            not the upload, and were the source of confusion. */}
                        <Box sx={{ p: 1.75 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <FileTextIcon size={12} color={theme.palette.text.tertiary} />
                            <Typography
                              variant="textXS_semiBold"
                              sx={{
                                color: 'text.tertiary',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                fontSize: 10.5,
                              }}
                            >
                              <Trans i18nKey="assessments.overview.uploadedFile" />
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                            <Box sx={{ color: theme.palette.green[700], display: 'flex' }}>
                              <CheckCircleIcon size={22} />
                            </Box>
                            <Typography
                              variant="textSM_semiBold"
                              sx={{ color: 'text.primary', wordBreak: 'break-word' }}
                            >
                              {aDoc.fileName}
                            </Typography>
                            {/* Per-document change tag — only when comparing */}
                            {isComparing && isNew && (
                              <Chip
                                size="small"
                                label={t('assessments.overview.versioning.documents.tag.new')}
                                sx={{
                                  height: 20, fontSize: 11, fontWeight: 600,
                                  backgroundColor: '#E8F8EF',
                                  color: theme.palette.green[700],
                                  border: '1px solid #BFE8CF',
                                  borderRadius: 999, '& .MuiChip-label': { px: 1 },
                                }}
                              />
                            )}
                            {isComparing && !isNew && docChange === DomainDocumentComparisonChangeType.Unchanged && (
                              <Chip
                                size="small"
                                label={t('assessments.overview.versioning.documents.tag.carriedOver')}
                                sx={{
                                  height: 20, fontSize: 11, fontWeight: 500,
                                  backgroundColor: '#F5F5F5',
                                  color: 'text.secondary',
                                  border: `1px solid ${theme.palette.grey[300]}`,
                                  borderRadius: 999, '& .MuiChip-label': { px: 1 },
                                }}
                              />
                            )}
                            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.25 }}>
                              <Typography variant="textXS_regular" sx={{ color: theme.palette.blue[700] }}>
                                {t('assessments.overview.uploadedOn', { date: uploadedDate, size: fileSizeKb })}
                              </Typography>
                              <Box
                                onClick={() => {
                                  if (downloadingDocId === aDoc.documentId) return;
                                  setDownloadingDocId(aDoc.documentId);
                                  downloadMutation.mutate(
                                    { documentId: aDoc.documentId },
                                    { onSettled: () => setDownloadingDocId(null) },
                                  );
                                }}
                                sx={{
                                  width: 28, height: 28, borderRadius: '6px',
                                  border: `1px solid ${theme.palette.grey[300]}`,
                                  backgroundColor: '#fff',
                                  color: 'text.secondary',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  cursor: downloadingDocId === aDoc.documentId ? 'default' : 'pointer',
                                }}
                              >
                                {downloadingDocId === aDoc.documentId
                                  ? <CircularProgress size={12} thickness={5} sx={{ color: 'text.secondary' }} />
                                  : <DownloadSimpleIcon size={14} />}
                              </Box>
                            </Box>
                          </Box>
                        </Box>

                        {/* === Classified as section ===
                            Bottom half describes which standard document the
                            uploaded file was matched against, plus its
                            document-type and weight. Distinct background +
                            "Classified as" header so users see two clearly
                            different things in one card. */}
                        {stdDoc && (
                          <Box
                            sx={{
                              p: 1.75,
                              backgroundColor: '#FAFAFC',
                              borderTop: `1px solid ${theme.palette.grey[200]}`,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 14, height: 14, borderRadius: '50%',
                                  backgroundColor: theme.palette.green[700],
                                  color: '#fff',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                <CheckCircleIcon size={9} weight="bold" />
                              </Box>
                              <Typography
                                variant="textXS_semiBold"
                                sx={{
                                  color: 'text.tertiary',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.06em',
                                  fontSize: 10.5,
                                }}
                              >
                                <Trans i18nKey="assessments.overview.classifiedAs" />
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography
                                variant="textSM_semiBold"
                                sx={{ color: 'text.primary', wordBreak: 'break-word' }}
                              >
                                {stdDoc.name}
                              </Typography>
                              <Box
                                sx={{
                                  color: 'text.tertiary',
                                  fontSize: 11.5,
                                  px: 0.75,
                                  py: 0.125,
                                  borderRadius: '4px',
                                  backgroundColor: '#F0F0F2',
                                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                  letterSpacing: '0.02em',
                                }}
                              >
                                {stdDoc.key}
                              </Box>
                              {stdDoc.documentType && (
                                <Chip
                                  size="small"
                                  label={stdDoc.documentType}
                                  sx={{
                                    height: 20, fontSize: 11.5, fontWeight: 500,
                                    backgroundColor: '#EFF8FF',
                                    color: theme.palette.blue[700],
                                    border: '1px solid #DAEEFF',
                                    borderRadius: 999, '& .MuiChip-label': { px: 1 },
                                  }}
                                />
                              )}
                              {stdDoc.weight != null && (
                                <Chip
                                  size="small"
                                  label={t('assessments.overview.weightBadge', { weight: (stdDoc.weight * 100).toFixed(1) })}
                                  sx={{
                                    height: 20, fontSize: 11.5, fontWeight: 600,
                                    backgroundColor: '#FAF5FD',
                                    color: theme.palette.violet[700],
                                    border: '1px solid #E8D4F8',
                                    borderRadius: 999, '& .MuiChip-label': { px: 1 },
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Removed documents — only when comparing */}
              {isComparing && removedDocs.length > 0 && (
                <Box sx={{ mt: domain.documents.length > 0 ? 2 : 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {removedDocs.map((entry, i) => {
                    const aDoc = entry.documentResult.document;
                    const fileSizeKb = (aDoc.fileSize / 1024).toFixed(1);
                    return (
                      <Box
                        key={`removed-${i}`}
                        sx={{
                          border: '1px dashed #FAC8C3',
                          backgroundColor: '#FFF7F6',
                          borderRadius: '12px',
                          p: 1.75,
                          opacity: 0.85,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                          <Box sx={{ color: theme.palette.error.main, display: 'flex' }}>
                            <ProhibitIcon size={22} />
                          </Box>
                          <Typography
                            variant="textSM_semiBold"
                            sx={{ color: 'text.primary', textDecoration: 'line-through' }}
                          >
                            {aDoc.fileName}
                          </Typography>
                          <Chip
                            size="small"
                            label={t('assessments.overview.versioning.documents.tag.removed')}
                            sx={{
                              height: 20, fontSize: 11, fontWeight: 600,
                              backgroundColor: '#FFE9E7',
                              color: theme.palette.error.main,
                              border: '1px solid #FAC8C3',
                              borderRadius: 999, '& .MuiChip-label': { px: 1 },
                            }}
                          />
                          <Box sx={{ ml: 'auto' }}>
                            <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
                              {t('assessments.overview.versioning.documents.removedFromBaseline', {
                                baseline: baselineLabel,
                                size: fileSizeKb,
                              })}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Missing standard documents */}
              {domain.missingDocumentsList.length > 0 && (
                <Box sx={{ mt: domain.documents.length > 0 ? 2 : 0 }}>
                  <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', display: 'block', mb: 1 }}>
                    <Trans i18nKey="assessments.overview.missingStandardDocuments" />
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {domain.missingDocumentsList.map((std, i) => (
                      <Box
                        key={i}
                        sx={{
                          border: '1px solid #FAD4BC',
                          backgroundColor: '#FFFBF2',
                          borderRadius: '12px',
                          p: 1.75,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                          <Box sx={{ color: '#E87040', display: 'flex' }}>
                            <InfoIcon size={22} />
                          </Box>
                          <Typography variant="textSM_semiBold" sx={{ color: 'text.primary' }}>
                            {std.name}
                          </Typography>
                          <Box
                            sx={{
                              color: 'text.tertiary',
                              fontSize: 11.5,
                              px: 0.75,
                              py: 0.125,
                              borderRadius: '4px',
                              backgroundColor: '#F5F5F5',
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                              letterSpacing: '0.02em',
                            }}
                          >
                            {std.key}
                          </Box>
                          {std.weight != null && (
                            <Chip
                              size="small"
                              label={t('assessments.overview.weightBadge', { weight: (std.weight * 100).toFixed(1) })}
                              sx={{
                                height: 20, fontSize: 11.5, fontWeight: 600,
                                backgroundColor: '#FAF5FD',
                                color: theme.palette.violet[700],
                                border: '1px solid #E8D4F8',
                                borderRadius: 999, '& .MuiChip-label': { px: 1 },
                              }}
                            />
                          )}
                          <Box sx={{ ml: 'auto' }}>
                            <Chip
                              size="small"
                              label={t('assessments.overview.missingBadge')}
                              sx={{
                                height: 24, fontSize: 11.5, fontWeight: 600,
                                backgroundColor: '#FAAD14', color: '#fff',
                                borderRadius: 999, '& .MuiChip-label': { px: 1.5 },
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
});
